import React, {
    useRef,
    useLayoutEffect,
    useState,
    useEffect,
    useContext
} from 'react';
import { useSpring, animated } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import CloseModal from '../../../assets/icons/close_modal.svg';
import backModal from '../../../assets/icons/back.svg';
import { ModalStackContext } from '../../../utils/ModalStackContext';

const ActionSheet = ({
                         children,
                         onCloseStart,
                         onCloseEnd,
                         disableSwipeToDismiss = false,
                         showBackButton = false,
                         onBackButtonClick
                     }) => {
    const sheetRef = useRef(null);
    const [sheetHeight, setSheetHeight] = useState(0);
    const [isClosing, setIsClosing] = useState(false);

    // Контекст стека
    const { stack, pushModal, popModal } = useContext(ModalStackContext);
    const sheetIdRef = useRef(Symbol('ActionSheet'));

    // Анимация «y» отвечает за сдвиг снизу вверх
    const [{ y }, api] = useSpring(() => ({
        y: window.innerHeight,
        config: { tension: 200, friction: 30 },
    }));

    const measureHeight = () => {
        if (sheetRef.current) {
            const measuredHeight = sheetRef.current.getBoundingClientRect().height;
            const viewportHeight = window.visualViewport
                ? window.visualViewport.height
                : window.innerHeight;

            // Ограничим максимально, чтобы шит не вылез за экран
            return Math.min(measuredHeight, viewportHeight - 40);
        }
        return 0;
    };

    useLayoutEffect(() => {
        const handleResize = () => {
            const newHeight = measureHeight();
            if (newHeight > 0) {
                setSheetHeight(newHeight);
            }
        };

        handleResize();

        window.addEventListener('resize', handleResize);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    // При монтировании «регистрируемся» в стеке
    useEffect(() => {
        const sheetId = sheetIdRef.current;
        pushModal(sheetId);
        return () => {
            popModal(sheetId);
        };
    }, [pushModal, popModal]);

    // Проверяем, что этот ActionSheet сейчас самый верхний
    const isOnTop =
        stack.length > 0 && stack[stack.length - 1] === sheetIdRef.current;

    // Анимация входа: когда компонент смонтировался и мы знаем высоту,
    // опускаем снизу в позицию 0
    useEffect(() => {
        if (sheetHeight > 0 && !isClosing) {
            // Сразу установим «начало» (y = sheetHeight), и потом сдвинем в 0
            api.set({ y: sheetHeight });
            requestAnimationFrame(() => {
                api.start({ y: 0 });
            });
        }
    }, [sheetHeight, api, isClosing]);

    // Главная функция закрытия с анимацией
    const closeSheet = () => {
        setIsClosing(true);
        if (onCloseStart) onCloseStart();

        // При закрытии сдвигаем шит обратно вниз
        const currentHeight = measureHeight();
        const targetY = currentHeight > 0 ? currentHeight : sheetHeight;

        api.start({
            y: targetY,
            onRest: () => {
                // Здесь анимация закончилась
                if (onCloseEnd) onCloseEnd();
            },
        });
    };

    // Обработка свайпа вниз для закрытия
    const bind = useDrag(({ down, movement: [_, my] }) => {
        // Если не верхний ActionSheet или свайп отключён
        if (!isOnTop || disableSwipeToDismiss) return;

        let newY = my;
        if (newY < 0) newY = 0;

        const currentHeight = measureHeight();
        const maxY = currentHeight > 0 ? currentHeight : sheetHeight;

        if (newY > maxY) {
            newY = maxY;
        }

        if (!down) {
            // Бросили свайп
            if (newY > maxY / 3) {
                // Если сдвинули достаточно, закрываем
                closeSheet();
            } else {
                // Возвращаем обратно
                api.start({ y: 0 });
            }
        } else {
            // Тянем ActionSheet при свайпе
            api.set({ y: newY });
        }
    });

    // Нажатие на «крестик» или «назад»
    const handleCloseClick = () => {
        // Если была кнопка «Назад», можем вызывать onBackButtonClick
        // иначе — стандартное закрытие
        if (showBackButton && onBackButtonClick) {
            onBackButtonClick();
        } else {
            closeSheet();
        }
    };

    return (
        <animated.div
            ref={sheetRef}
            {...bind()}
            style={{
                touchAction: 'none',
                transform: y.to(val => `translateY(${val}px)`),
            }}
            className="action-sheet"
        >
            <div className="action-sheet__handle" />

            {typeof children === 'function'
                ? children(closeSheet)  // передаём внутрь
                : children}

            <button onClick={handleCloseClick} className="action-sheet__close">
                <img
                    src={showBackButton ? backModal : CloseModal}
                    alt="Close Modal"
                />
            </button>
        </animated.div>
    );
};

export default ActionSheet;
